/**
 * @fileoverview The student's practice streak, trend and milestones (F3).
 *
 * Totals do not motivate; trends and streaks do. That is the whole thesis of
 * Spec 02, and it shapes what this component chooses to show: the streak first
 * and largest, then the eight-week shape of the habit, then the next thing to
 * aim at — never a raw lifetime total, which is a number that only ever grows
 * and therefore says nothing about this week.
 *
 * Two deliberate restraints. There is no comparison to anybody else (decision
 * #6: the motivation research is mixed for children and it builds a
 * COPPA-adjacent comparison surface). And an empty week is drawn as an empty
 * week — a chart that omits the days somebody did nothing is a chart that
 * flatters, and a student can tell.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import studentApi from "@/lib/studentApi";
import { Card, SectionHeader, StatCard } from "@/components/studio/primitives";

/** Sunday-first weekday initials for the axis. */
const DAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * @param {Object} [props]
 * @param {Object|null} [props.stats] - Stats the page has already read (Progress,
 *   C7); without them the card reads its own.
 */
export function StudentPracticeStreak({ stats: given } = {}) {
  const [read, setStats] = useState(null);
  const ownRead = given === undefined;
  const stats = ownRead ? read : given;

  useEffect(() => {
    if (!ownRead) return undefined;
    let alive = true;
    studentApi
      .get("/student/stats")
      .then(({ data }) => {
        if (!alive) return;
        setStats(data);
        // The celebration arrives once, on the read that first notices it.
        (data.newly_awarded || []).forEach((m) => toast.success(m.title));
      })
      .catch(() => {
        /* A missing streak is not worth a red toast on a dashboard. */
      });
    return () => {
      alive = false;
    };
  }, [ownRead]);

  if (!stats) return null;

  const series = stats.series || [];
  const peak = Math.max(1, ...series.map((d) => d.minutes));
  // Last four weeks only on screen: eight is the window the server computes so
  // the shape is stable, but four is what fits without the bars becoming hairs.
  const shown = series.slice(-28);

  return (
    <Card>
      <SectionHeader>Your practice</SectionHeader>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          data-testid="practice-streak"
          value={stats.current_streak_days}
          label={stats.current_streak_days === 1 ? "day in a row" : "days in a row"}
          sub={stats.longest_streak_days > stats.current_streak_days ? `best: ${stats.longest_streak_days}` : "keep going"}
        />
        <StatCard value={stats.week_minutes} label="minutes" sub="in the last 7 days" />
        <StatCard value={stats.total_sessions} label="sessions" sub="all time" />
      </div>

      <div className="mt-6">
        <div className="flex items-end gap-[3px]" style={{ height: 90 }} data-testid="practice-chart">
          {shown.map((day) => (
            <div
              key={day.date}
              title={`${day.date}: ${day.minutes} min`}
              className={day.minutes > 0 ? "flex-1 rounded-t bg-[var(--accent-yellow)]" : "flex-1 rounded-t bg-[var(--studio-divider)]"}
              style={{ height: `${Math.max(2, Math.round((day.minutes / peak) * 80))}px` }}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between font-mono text-[10px] text-[var(--text-tertiary)]">
          {shown
            .filter((_, i) => i % 7 === 0)
            .map((day) => (
              <span key={day.date}>{DAY_INITIALS[new Date(`${day.date}T00:00:00`).getDay()]}</span>
            ))}
        </div>
      </div>

      {stats.next_milestone ? (
        <p className="mt-4 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="practice-next">
          Next up: {stats.next_milestone.title}
        </p>
      ) : null}
    </Card>
  );
}

export default StudentPracticeStreak;
